export type MunicipalityQueryContext = {
  codIbgeMunicipio?: number;
  municipalityName?: string;
  isMunicipalityHome: boolean;
  municipioQuerySuffix: string;
};

export function parseCodIbgeMunicipio(value: unknown): number | undefined {
  const raw = Array.isArray(value) ? value[0] : value;
  if (raw == null || String(raw).trim() === "") {
    return undefined;
  }

  const cod = Number(raw);
  if (!Number.isInteger(cod) || cod <= 0) {
    return undefined;
  }

  return cod;
}

export function buildMunicipioQuerySuffix(
  codIbgeMunicipio?: number,
  municipalityName?: string
): string {
  if (!codIbgeMunicipio || !municipalityName) {
    return "";
  }

  const nomeEncoded = encodeURIComponent(municipalityName);
  return `?municipio=${codIbgeMunicipio}&nome=${nomeEncoded}`;
}

function toUrlSearchParams(search: string): URLSearchParams {
  const normalized = search.startsWith("?") ? search.slice(1) : search;
  return new URLSearchParams(normalized);
}

/**
 * Contexto da home municipal: exige `municipio` e `nome` na query (comportamento do provider).
 */
export function parseMunicipalityHomeFromSearch(
  search: string
): MunicipalityQueryContext {
  const params = toUrlSearchParams(search);
  const codIbgeMunicipio = parseCodIbgeMunicipio(params.get("municipio"));
  const nomeFromQuery = params.get("nome");
  const municipalityName = nomeFromQuery
    ? decodeURIComponent(nomeFromQuery)
    : undefined;
  const isMunicipalityHome = Boolean(codIbgeMunicipio && municipalityName);

  return {
    codIbgeMunicipio,
    municipalityName,
    isMunicipalityHome,
    municipioQuerySuffix: isMunicipalityHome
      ? buildMunicipioQuerySuffix(codIbgeMunicipio, municipalityName)
      : "",
  };
}

/**
 * Contexto das telas de listagem/busca: aceita `municipio` na rota ou na query;
 * `nome` opcional (fallback para o próprio código IBGE).
 */
export function resolveListMunicipalityContext(
  routeMunicipio?: string,
  locationSearch = ""
): Pick<
  MunicipalityQueryContext,
  "codIbgeMunicipio" | "municipalityName" | "municipioQuerySuffix"
> {
  const params = toUrlSearchParams(locationSearch);
  const codIbgeMunicipio =
    parseCodIbgeMunicipio(routeMunicipio) ??
    parseCodIbgeMunicipio(params.get("municipio"));

  const nomeFromQuery = params.get("nome");
  let municipalityName: string | undefined;
  if (codIbgeMunicipio) {
    municipalityName = nomeFromQuery
      ? decodeURIComponent(nomeFromQuery)
      : String(codIbgeMunicipio);
  }

  return {
    codIbgeMunicipio,
    municipalityName,
    municipioQuerySuffix: buildMunicipioQuerySuffix(
      codIbgeMunicipio,
      municipalityName
    ),
  };
}

/**
 * SSR / Express: lê `municipio` já parseado em `req.query`.
 */
export function parseMunicipalityFromExpressQuery(query: {
  municipio?: unknown;
  nome?: unknown;
}): Pick<MunicipalityQueryContext, "codIbgeMunicipio" | "municipalityName"> {
  const codIbgeMunicipio = parseCodIbgeMunicipio(query.municipio);
  const nomeRaw = Array.isArray(query.nome) ? query.nome[0] : query.nome;
  const municipalityName =
    typeof nomeRaw === "string" && nomeRaw.trim() !== ""
      ? decodeURIComponent(nomeRaw)
      : undefined;

  return { codIbgeMunicipio, municipalityName };
}
